/**
 * MindMap Supabase Service
 *
 * Database wrapper for all Supabase operations including:
 * - CRUD for maps, nodes, edges
 * - Vector similarity search via RPC
 * - Time travel queries
 * - Authentication helpers
 */

import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

// =====================================================
// USER CACHE - Prevents repeated getUser() API calls
// =====================================================
let cachedUser = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached user or fetch from API if cache expired
 */
export async function getCachedUser() {
  const now = Date.now();

  // Return cached user if still valid
  if (cachedUser && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedUser;
  }

  // Fetch fresh user data
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    cachedUser = null;
    return null;
  }

  cachedUser = user;
  cacheTimestamp = now;
  return user;
}

/**
 * Clear user cache (call on sign out)
 */
export function clearUserCache() {
  cachedUser = null;
  cacheTimestamp = 0;
}

// Listen for auth state changes to update cache
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') {
    clearUserCache();
  } else if (event === 'SIGNED_IN' && session?.user) {
    cachedUser = session.user;
    cacheTimestamp = Date.now();
  }
});

// =====================================================
// AUTHENTICATION
// =====================================================

export const auth = {
  /**
   * Sign up a new user
   */
  async signUp(email, password) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
    return data;
  },

  /**
   * Sign in with email and password
   */
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  },

  /**
   * Sign in with OAuth provider
   */
  async signInWithProvider(provider) {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    if (error) throw error;
    return data;
  },

  /**
   * Sign out
   */
  async signOut() {
    try {
      // Use scope: 'local' to avoid server request when session is invalid
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      // Ignore errors - session might already be invalid
      console.warn('SignOut warning:', error.message);
    }
  },

  /**
   * Get current session
   */
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  },

  /**
   * Get current user
   */
  async getUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  },

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  },
};

// =====================================================
// PROFILES
// =====================================================

export const profiles = {
  /**
   * Get user profile
   */
  async get(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Update user profile
   */
  async update(userId, updates) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Create profile (called on signup)
   */
  async create(userId, profileData) {
    const { data, error } = await supabase
      .from('profiles')
      .insert({ id: userId, ...profileData })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Upload avatar image
   */
  async uploadAvatar(userId, file) {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/avatar.${fileExt}`;

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(fileName, file, { upsert: true });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(fileName);

    // Update profile with avatar URL
    const { data, error } = await supabase
      .from('profiles')
      .update({ avatar_url: publicUrl })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get coach profile for a user
   */
  async getCoachProfile(userId) {
    const { data, error } = await supabase
      .from('coach_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Create or update coach profile
   */
  async upsertCoachProfile(userId, profileData) {
    // Check if profile exists
    const existing = await this.getCoachProfile(userId);

    if (existing) {
      // Update existing
      const { data, error } = await supabase
        .from('coach_profiles')
        .update({ ...profileData, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .select()
        .single();
      if (error) throw error;
      return data;
    } else {
      // Create new
      const { data, error } = await supabase
        .from('coach_profiles')
        .insert({ user_id: userId, ...profileData })
        .select()
        .single();
      if (error) throw error;
      return data;
    }
  },
};

// =====================================================
// MAPS
// =====================================================

export const maps = {
  /**
   * Get all maps for current user
   */
  async getAll() {
    const { data, error } = await supabase
      .from('maps')
      .select('*')
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * Get a single map by ID
   */
  async get(mapId) {
    const { data, error } = await supabase
      .from('maps')
      .select('*')
      .eq('id', mapId)
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Get the user's main map
   */
  async getMain(userId) {
    const { data, error } = await supabase
      .from('maps')
      .select('*')
      .eq('user_id', userId)
      .eq('is_main', true)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  /**
   * Create a new map
   */
  async create(mapData) {
    const { data, error } = await supabase
      .from('maps')
      .insert(mapData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Update a map
   */
  async update(mapId, updates) {
    const { data, error } = await supabase
      .from('maps')
      .update(updates)
      .eq('id', mapId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Delete a map
   */
  async delete(mapId) {
    const { error } = await supabase
      .from('maps')
      .delete()
      .eq('id', mapId);
    if (error) throw error;
  },

  /**
   * Get map statistics
   */
  async getStats(mapId) {
    const { data, error } = await supabase
      .rpc('get_map_stats', { target_map_id: mapId });
    if (error) throw error;
    return data?.[0];
  },
};

// =====================================================
// NODES
// =====================================================

export const nodes = {
  /**
   * Get all nodes in a map
   */
  async getAll(mapId) {
    const { data, error } = await supabase
      .from('nodes')
      .select('*')
      .eq('map_id', mapId)
      .eq('is_approved', true)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data;
  },

  /**
   * Get all nodes including ghost nodes
   */
  async getAllWithGhosts(mapId) {
    const { data, error } = await supabase
      .from('nodes')
      .select('*, attachments(*)')
      .eq('map_id', mapId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data;
  },

  /**
   * Get a single node
   */
  async get(nodeId) {
    const { data, error } = await supabase
      .from('nodes')
      .select('*')
      .eq('id', nodeId)
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Create a new node
   */
  async create(nodeData) {
    const { data, error } = await supabase
      .from('nodes')
      .insert(nodeData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Create a node with embedding (vector format for pgvector)
   */
  async createWithEmbedding(nodeData, embedding) {
    // Format embedding as a string for pgvector
    const embeddingStr = `[${embedding.join(',')}]`;

    const { data, error } = await supabase
      .from('nodes')
      .insert({
        ...nodeData,
        embedding: embeddingStr,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Update a node
   */
  async update(nodeId, updates) {
    const { data, error } = await supabase
      .from('nodes')
      .update(updates)
      .eq('id', nodeId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Update node position
   */
  async updatePosition(nodeId, position) {
    const { data, error } = await supabase
      .from('nodes')
      .update({ position })
      .eq('id', nodeId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Batch update positions (for drag operations)
   */
  async batchUpdatePositions(updates) {
    const promises = updates.map(({ id, position }) =>
      supabase
        .from('nodes')
        .update({ position })
        .eq('id', id)
    );
    await Promise.all(promises);
  },

  /**
   * Delete a node
   */
  async delete(nodeId) {
    const { error } = await supabase
      .from('nodes')
      .delete()
      .eq('id', nodeId);
    if (error) throw error;
  },

  /**
   * Approve a ghost node
   */
  async approve(nodeId) {
    const { data, error } = await supabase
      .from('nodes')
      .update({ is_approved: true, type: 'text' })
      .eq('id', nodeId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Get unique cluster tags in a map
   */
  async getClusters(mapId) {
    const { data, error } = await supabase
      .from('nodes')
      .select('cluster_tag')
      .eq('map_id', mapId)
      .eq('is_approved', true)
      .not('cluster_tag', 'is', null);

    if (error) throw error;

    // Get unique clusters with counts
    const clusters = {};
    data.forEach(n => {
      if (n.cluster_tag) {
        clusters[n.cluster_tag] = (clusters[n.cluster_tag] || 0) + 1;
      }
    });

    return Object.entries(clusters).map(([tag, count]) => ({ tag, count }));
  },
};

// =====================================================
// VECTOR SEARCH (The Heart of Semantic Features)
// =====================================================

export const vectorSearch = {
  /**
   * Find semantically similar nodes using cosine similarity
   */
  async matchNodes(embedding, mapId, options = {}) {
    const {
      threshold = 0.7,
      limit = 5,
      excludeNodeId = null,
    } = options;

    // Format embedding as a string for pgvector
    const embeddingStr = `[${embedding.join(',')}]`;

    const { data, error } = await supabase.rpc('match_nodes', {
      query_embedding: embeddingStr,
      match_map_id: mapId,
      match_threshold: threshold,
      match_count: limit,
      exclude_node_id: excludeNodeId,
    });

    if (error) throw error;
    return data;
  },

  /**
   * Find nodes across multiple maps (for submap sync)
   */
  async matchNodesCrossMap(embedding, mapIds, options = {}) {
    const {
      threshold = 0.6,
      limit = 10,
      excludeNodeId = null,
    } = options;

    const embeddingStr = `[${embedding.join(',')}]`;

    const { data, error } = await supabase.rpc('match_nodes_cross_map', {
      query_embedding: embeddingStr,
      map_ids: mapIds,
      match_threshold: threshold,
      match_count: limit,
      exclude_node_id: excludeNodeId,
    });

    if (error) throw error;
    return data;
  },

  /**
   * Get context for RAG chat
   */
  async getMapContext(embedding, mapId, contextCount = 10) {
    const embeddingStr = `[${embedding.join(',')}]`;

    const { data, error } = await supabase.rpc('get_map_context', {
      query_embedding: embeddingStr,
      target_map_id: mapId,
      context_count: contextCount,
    });

    if (error) throw error;
    return data;
  },

  /**
   * Find semantically similar attachments in a map
   */
  async matchAttachmentsInMap(embedding, mapId, options = {}) {
    const {
      threshold = 0.7,
      limit = 10,
    } = options;

    const embeddingStr = `[${embedding.join(',')}]`;

    const { data, error } = await supabase.rpc('match_attachments_in_map', {
      query_embedding: embeddingStr,
      target_map_id: mapId,
      match_threshold: threshold,
      match_count: limit,
    });

    if (error) throw error;
    return data;
  },

  /**
   * Find potential clusters
   */
  async findClusterCandidates(mapId, threshold = 0.75) {
    const { data, error } = await supabase.rpc('find_cluster_candidates', {
      target_map_id: mapId,
      similarity_threshold: threshold,
    });

    if (error) throw error;
    return data;
  },

  // =====================================================
  // GLOBAL SEARCH (across all user's maps)
  // =====================================================

  /**
   * Search nodes across ALL user's maps
   */
  async matchNodesGlobal(embedding, options = {}) {
    const {
      threshold = 0.6,
      limit = 15,
    } = options;

    const embeddingStr = `[${embedding.join(',')}]`;

    const { data, error } = await supabase.rpc('match_nodes_global', {
      query_embedding: embeddingStr,
      match_threshold: threshold,
      match_count: limit,
    });

    if (error) throw error;
    return data;
  },

  /**
   * Search attachments across ALL user's maps
   */
  async matchAttachmentsGlobal(embedding, options = {}) {
    const {
      threshold = 0.6,
      limit = 10,
    } = options;

    const embeddingStr = `[${embedding.join(',')}]`;

    const { data, error } = await supabase.rpc('match_attachments_global', {
      query_embedding: embeddingStr,
      match_threshold: threshold,
      match_count: limit,
    });

    if (error) throw error;
    return data;
  },
};

// =====================================================
// EDGES
// =====================================================

export const edges = {
  /**
   * Get all edges in a map
   */
  async getAll(mapId) {
    const { data, error } = await supabase
      .from('edges')
      .select('*')
      .eq('map_id', mapId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data;
  },

  /**
   * Create a new edge
   */
  async create(edgeData) {
    const { data, error } = await supabase
      .from('edges')
      .insert(edgeData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Create multiple edges (for AI-generated connections)
   */
  async createMany(edgesData) {
    const { data, error } = await supabase
      .from('edges')
      .insert(edgesData)
      .select();
    if (error) throw error;
    return data;
  },

  /**
   * Update an edge
   */
  async update(edgeId, updates) {
    const { data, error } = await supabase
      .from('edges')
      .update(updates)
      .eq('id', edgeId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Delete an edge
   */
  async delete(edgeId) {
    const { error } = await supabase
      .from('edges')
      .delete()
      .eq('id', edgeId);
    if (error) throw error;
  },

  /**
   * Delete edges for a node
   */
  async deleteForNode(nodeId) {
    const { error } = await supabase
      .from('edges')
      .delete()
      .or(`source_id.eq.${nodeId},target_id.eq.${nodeId}`);
    if (error) throw error;
  },
};

// =====================================================
// TIME TRAVEL
// =====================================================

export const timeTravel = {
  /**
   * Get the map state at a specific timestamp
   */
  async getNodesAtTime(mapId, timestamp) {
    const { data, error } = await supabase.rpc('get_nodes_at_timestamp', {
      target_map_id: mapId,
      target_timestamp: timestamp,
    });
    if (error) throw error;
    return data;
  },

  /**
   * Get edges at a specific timestamp
   */
  async getEdgesAtTime(mapId, timestamp) {
    const { data, error } = await supabase.rpc('get_edges_at_timestamp', {
      target_map_id: mapId,
      target_timestamp: timestamp,
    });
    if (error) throw error;
    return data;
  },

  /**
   * Get the time range for a map
   */
  async getTimeRange(mapId) {
    const { data, error } = await supabase
      .from('node_history')
      .select('created_at')
      .eq('map_id', mapId)
      .order('created_at', { ascending: true });

    if (error) throw error;

    if (data.length === 0) {
      return { start: new Date(), end: new Date() };
    }

    return {
      start: new Date(data[0].created_at),
      end: new Date(data[data.length - 1].created_at),
    };
  },

  /**
   * Get history events for timeline
   */
  async getHistoryEvents(mapId, limit = 50) {
    const { data, error } = await supabase
      .from('node_history')
      .select('*')
      .eq('map_id', mapId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },
};

// =====================================================
// CHAT SESSIONS & MESSAGES
// =====================================================

export const chat = {
  // =====================================================
  // SESSIONS
  // =====================================================

  /**
   * Get all chat sessions for a map
   */
  async getSessions(mapId) {
    const { data, error } = await supabase
      .from('chat_sessions')
      .select('*')
      .eq('map_id', mapId)
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * Create a new chat session
   */
  async createSession(mapId, userId, title = 'New Chat') {
    const { data, error } = await supabase
      .from('chat_sessions')
      .insert({ map_id: mapId, user_id: userId, title })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Update session (title, etc.)
   */
  async updateSession(sessionId, updates) {
    const { data, error } = await supabase
      .from('chat_sessions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', sessionId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Delete a chat session (cascades to messages)
   */
  async deleteSession(sessionId) {
    const { error } = await supabase
      .from('chat_sessions')
      .delete()
      .eq('id', sessionId);
    if (error) throw error;
  },

  // =====================================================
  // MESSAGES
  // =====================================================

  /**
   * Get all messages for a session
   */
  async getMessages(sessionId) {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data;
  },

  /**
   * Get messages for a map (legacy - returns all)
   */
  async getMessagesForMap(mapId, limit = 50) {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('map_id', mapId)
      .order('created_at', { ascending: true })
      .limit(limit);
    if (error) throw error;
    return data;
  },

  /**
   * Add a chat message to a session
   */
  async addMessage(messageData) {
    const { data, error } = await supabase
      .from('chat_messages')
      .insert(messageData)
      .select()
      .single();
    if (error) throw error;

    // Update session's updated_at
    if (messageData.session_id) {
      await supabase
        .from('chat_sessions')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', messageData.session_id);
    }

    return data;
  },

  /**
   * Clear chat history for a map (legacy)
   */
  async clearHistory(mapId) {
    const { error } = await supabase
      .from('chat_messages')
      .delete()
      .eq('map_id', mapId);
    if (error) throw error;
  },
};

// =====================================================
// SUBMAP LINKS
// =====================================================

export const submaps = {
  /**
   * Get submaps linked to a parent map
   */
  async getLinked(parentMapId) {
    const { data, error } = await supabase
      .from('submap_links')
      .select(`
        *,
        child_map:maps!child_map_id(*)
      `)
      .eq('parent_map_id', parentMapId);
    if (error) throw error;
    return data;
  },

  /**
   * Link a submap to a parent
   */
  async link(parentMapId, childMapId, position) {
    const { data, error } = await supabase
      .from('submap_links')
      .insert({
        parent_map_id: parentMapId,
        child_map_id: childMapId,
        position,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Unlink a submap
   */
  async unlink(linkId) {
    const { error } = await supabase
      .from('submap_links')
      .delete()
      .eq('id', linkId);
    if (error) throw error;
  },

  /**
   * Mark submap as synced
   */
  async markSynced(linkId) {
    const { data, error } = await supabase
      .from('submap_links')
      .update({ synced_at: new Date().toISOString() })
      .eq('id', linkId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// =====================================================
// ATTACHMENTS
// =====================================================

export const attachments = {
  /**
   * Upload an attachment for a node
   * @param {string} nodeId - The node ID to attach to
   * @param {File} file - The file to upload
   * @param {Object} aiData - Optional AI-extracted data (extracted_text, summary, embedding)
   */
  async upload(nodeId, file, aiData = {}) {
    // Get current user
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    // Generate unique file path
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 11)}.${fileExt}`;
    const filePath = `${user.id}/${nodeId}/${fileName}`;

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from('attachments')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('attachments')
      .getPublicUrl(filePath);

    // Save attachment record with AI data
    const { data, error } = await supabase
      .from('attachments')
      .insert({
        node_id: nodeId,
        user_id: user.id,
        file_name: file.name,
        file_url: publicUrl,
        file_type: file.type,
        file_size: file.size,
        storage_path: filePath,
        extracted_text: aiData.extracted_text || null,
        summary: aiData.summary || null,
        embedding: aiData.embedding || null,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get attachments for a node
   */
  async getForNode(nodeId) {
    const { data, error } = await supabase
      .from('attachments')
      .select('*')
      .eq('node_id', nodeId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * Delete an attachment
   */
  async delete(attachmentId) {
    // First get the attachment to get storage path
    const { data: attachment, error: fetchError } = await supabase
      .from('attachments')
      .select('storage_path')
      .eq('id', attachmentId)
      .single();

    if (fetchError) throw fetchError;

    // Delete from storage
    if (attachment?.storage_path) {
      const { error: storageError } = await supabase.storage
        .from('attachments')
        .remove([attachment.storage_path]);

      if (storageError) console.warn('Storage delete error:', storageError);
    }

    // Delete record
    const { error } = await supabase
      .from('attachments')
      .delete()
      .eq('id', attachmentId);

    if (error) throw error;
  },
};

// =====================================================
// CASHFLOW
// =====================================================

export const cashflow = {
  /**
   * Get cashflow entries for a date range
   */
  async getEntries(startDate, endDate) {
    const { data, error } = await supabase
      .from('cashflow_entries')
      .select('*')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });
    if (error) throw error;
    return data;
  },

  /**
   * Get all entries for current user
   */
  async getAll() {
    const { data, error } = await supabase
      .from('cashflow_entries')
      .select('*')
      .order('date', { ascending: true });
    if (error) throw error;
    return data;
  },

  /**
   * Get a single entry
   */
  async get(entryId) {
    const { data, error } = await supabase
      .from('cashflow_entries')
      .select('*')
      .eq('id', entryId)
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Create a new entry
   */
  async create(entryData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('cashflow_entries')
      .insert({ ...entryData, user_id: user.id })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Update an entry
   */
  async update(entryId, updates) {
    const { data, error } = await supabase
      .from('cashflow_entries')
      .update(updates)
      .eq('id', entryId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Delete an entry
   */
  async delete(entryId) {
    const { error } = await supabase
      .from('cashflow_entries')
      .delete()
      .eq('id', entryId);
    if (error) throw error;
  },

  /**
   * Mark entry as completed
   */
  async markCompleted(entryId, completedAmount = null) {
    const { data, error } = await supabase
      .from('cashflow_entries')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        completed_amount: completedAmount,
      })
      .eq('id', entryId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Mark entry as pending (undo complete)
   */
  async markPending(entryId) {
    const { data, error } = await supabase
      .from('cashflow_entries')
      .update({
        status: 'pending',
        completed_at: null,
        completed_amount: null,
      })
      .eq('id', entryId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Reschedule an entry
   */
  async reschedule(entryId, newDate) {
    const { data, error } = await supabase
      .from('cashflow_entries')
      .update({
        date: newDate,
        status: 'pending',
      })
      .eq('id', entryId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Get overdue entries
   */
  async getOverdue() {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('cashflow_entries')
      .select('*')
      .eq('status', 'pending')
      .lt('date', today)
      .order('date', { ascending: true });
    if (error) throw error;
    return data;
  },

  /**
   * Get recurring parent entries
   */
  async getRecurringParents() {
    const { data, error } = await supabase
      .from('cashflow_entries')
      .select('*')
      .eq('is_recurring', true)
      .is('recurring_parent_id', null)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * Create instance from recurring parent
   */
  async createRecurringInstance(parentId, date) {
    // Get parent entry
    const { data: parent, error: parentError } = await supabase
      .from('cashflow_entries')
      .select('*')
      .eq('id', parentId)
      .single();

    if (parentError) throw parentError;

    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    // Create instance
    const { data, error } = await supabase
      .from('cashflow_entries')
      .insert({
        user_id: user.id,
        title: parent.title,
        description: parent.description,
        amount: parent.amount,
        currency: parent.currency,
        type: parent.type,
        category: parent.category,
        account_name: parent.account_name,
        account_type: parent.account_type,
        date: date,
        is_recurring: false,
        recurring_parent_id: parentId,
        status: 'pending',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get summary stats for a date range
   */
  async getSummary(startDate, endDate) {
    const { data, error } = await supabase
      .from('cashflow_entries')
      .select('amount, currency, type, status')
      .gte('date', startDate)
      .lte('date', endDate);

    if (error) throw error;

    const summary = {
      totalIncome: 0,
      totalExpense: 0,
      netAmount: 0,
      pendingCount: 0,
      overdueCount: 0,
      completedCount: 0,
      byCurrency: {},
    };

    data.forEach(entry => {
      const amount = parseFloat(entry.amount);
      const currency = entry.currency || 'TRY';

      // Initialize currency bucket
      if (!summary.byCurrency[currency]) {
        summary.byCurrency[currency] = { income: 0, expense: 0, net: 0 };
      }

      if (entry.type === 'income') {
        summary.byCurrency[currency].income += amount;
      } else {
        summary.byCurrency[currency].expense += amount;
      }

      summary.byCurrency[currency].net =
        summary.byCurrency[currency].income - summary.byCurrency[currency].expense;

      // Status counts
      if (entry.status === 'pending') {
        summary.pendingCount++;
      } else if (entry.status === 'completed') {
        summary.completedCount++;
      } else if (entry.status === 'overdue') {
        summary.overdueCount++;
      }
    });

    // Calculate TRY totals as primary
    if (summary.byCurrency['TRY']) {
      summary.totalIncome = summary.byCurrency['TRY'].income;
      summary.totalExpense = summary.byCurrency['TRY'].expense;
      summary.netAmount = summary.byCurrency['TRY'].net;
    }

    return summary;
  },

  /**
   * Create a loan group with all installments
   * @param {Object} loanInfo - Loan metadata (creditor, totalAmount, etc.)
   * @param {Array} installments - Array of installment objects
   * @returns {Object} - The first installment (group parent) and all created entries
   */
  async createLoanGroup(loanInfo, installments) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    if (!installments || installments.length === 0) {
      throw new Error('No installments provided');
    }

    // First, create the first installment (it will be the group parent)
    const firstInstallment = installments[0];
    const { data: parent, error: parentError } = await supabase
      .from('cashflow_entries')
      .insert({
        user_id: user.id,
        title: firstInstallment.title,
        description: firstInstallment.description || null,
        amount: firstInstallment.amount,
        currency: firstInstallment.currency || loanInfo.currency || 'TRY',
        type: 'expense',
        category: 'loan',
        date: firstInstallment.due_date,
        is_recurring: false,
        status: firstInstallment.status || 'pending',
        creditor: loanInfo.creditor || null,
        account_name: loanInfo.account_name || null,
        account_type: loanInfo.account_type || null,
        installment_number: 1,
        total_installments: installments.length,
        principal_amount: firstInstallment.principal || null,
        interest_amount: firstInstallment.interest || null,
        metadata: {
          imported_from: loanInfo.source || 'file_import',
          loan_type: loanInfo.loan_type || null,
          interest_rate: loanInfo.interest_rate || null,
          total_loan_amount: loanInfo.totalAmount || null,
        },
      })
      .select()
      .single();

    if (parentError) throw parentError;

    // Now create remaining installments, all pointing to the parent's ID as loan_group_id
    const remainingInstallments = installments.slice(1);
    const createdEntries = [parent];

    if (remainingInstallments.length > 0) {
      const installmentData = remainingInstallments.map((inst, idx) => ({
        user_id: user.id,
        title: inst.title,
        description: inst.description || null,
        amount: inst.amount,
        currency: inst.currency || loanInfo.currency || 'TRY',
        type: 'expense',
        category: 'loan',
        date: inst.due_date,
        is_recurring: false,
        status: inst.status || 'pending',
        creditor: loanInfo.creditor || null,
        account_name: loanInfo.account_name || null,
        account_type: loanInfo.account_type || null,
        loan_group_id: parent.id,
        installment_number: idx + 2, // Starting from 2
        total_installments: installments.length,
        principal_amount: inst.principal || null,
        interest_amount: inst.interest || null,
        metadata: {
          imported_from: loanInfo.source || 'file_import',
          loan_type: loanInfo.loan_type || null,
        },
      }));

      const { data: children, error: childrenError } = await supabase
        .from('cashflow_entries')
        .insert(installmentData)
        .select();

      if (childrenError) throw childrenError;
      createdEntries.push(...children);
    }

    // Update the parent to also have loan_group_id pointing to itself (for consistent queries)
    await supabase
      .from('cashflow_entries')
      .update({ loan_group_id: parent.id })
      .eq('id', parent.id);

    return {
      groupId: parent.id,
      entries: createdEntries,
      count: createdEntries.length,
    };
  },

  /**
   * Get all entries in a loan group
   */
  async getLoanGroupEntries(groupId) {
    const { data, error } = await supabase
      .from('cashflow_entries')
      .select('*')
      .eq('loan_group_id', groupId)
      .order('installment_number', { ascending: true });

    if (error) throw error;
    return data;
  },

  /**
   * Delete an entire loan group
   */
  async deleteLoanGroup(groupId) {
    // Due to ON DELETE CASCADE, deleting entries with loan_group_id = groupId
    // will be handled by the database when we delete the parent
    // But we need to delete all entries with this loan_group_id first
    const { error } = await supabase
      .from('cashflow_entries')
      .delete()
      .eq('loan_group_id', groupId);

    if (error) throw error;
  },

  /**
   * Get all loan groups with their installments
   */
  async getLoanGroups() {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    // Get all entries that are part of loan groups
    const { data: allLoanEntries, error } = await supabase
      .from('cashflow_entries')
      .select('*')
      .eq('user_id', user.id)
      .not('loan_group_id', 'is', null)
      .order('installment_number', { ascending: true });

    if (error) throw error;

    // Group by loan_group_id
    const groupMap = {};
    allLoanEntries.forEach(entry => {
      const groupId = entry.loan_group_id;
      if (!groupMap[groupId]) {
        groupMap[groupId] = {
          id: groupId,
          installments: [],
        };
      }
      groupMap[groupId].installments.push(entry);
    });

    return Object.values(groupMap);
  },

  /**
   * Update shared fields across all entries in a loan group
   * @param {string} groupId - The loan_group_id
   * @param {Object} fields - Fields to update (account_name, account_type, creditor)
   */
  async updateLoanGroupSharedFields(groupId, fields) {
    const allowedFields = ['account_name', 'account_type', 'creditor'];
    const updateData = {};

    for (const key of allowedFields) {
      if (fields[key] !== undefined) {
        updateData[key] = fields[key];
      }
    }

    if (Object.keys(updateData).length === 0) {
      return { updated: 0 };
    }

    const { data, error } = await supabase
      .from('cashflow_entries')
      .update(updateData)
      .eq('loan_group_id', groupId)
      .select();

    if (error) throw error;
    return { updated: data?.length || 0 };
  },

  /**
   * Check if an entry is part of a loan group
   */
  async isPartOfLoanGroup(entryId) {
    const { data, error } = await supabase
      .from('cashflow_entries')
      .select('loan_group_id, installment_number, total_installments')
      .eq('id', entryId)
      .single();

    if (error) throw error;
    return {
      isGrouped: !!data.loan_group_id,
      groupId: data.loan_group_id,
      installmentNumber: data.installment_number,
      totalInstallments: data.total_installments,
    };
  },

  // =====================================================
  // FINANCIAL ANALYSIS
  // =====================================================

  /**
   * Get saved analysis for a specific month
   * @param {string} month - Format: YYYY-MM
   */
  async getAnalysis(month) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('cashflow_analyses')
      .select('*')
      .eq('user_id', user.id)
      .eq('analysis_month', month)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Save or update analysis for a specific month
   * @param {string} month - Format: YYYY-MM
   * @param {Object} analysis - Analysis results from AI
   * @param {Object} dataSnapshot - Optional data snapshot
   */
  async saveAnalysis(month, analysis, dataSnapshot = null) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const analysisData = {
      user_id: user.id,
      analysis_month: month,
      overall_health: analysis.overallHealth,
      health_score: analysis.healthScore,
      summary: analysis.summary,
      sections: analysis.sections,
      key_metrics: analysis.keyMetrics,
      recommendations: analysis.recommendations,
      alerts: analysis.alerts,
      data_snapshot: dataSnapshot,
      updated_at: new Date().toISOString(),
    };

    // Upsert - insert or update if exists
    const { data, error } = await supabase
      .from('cashflow_analyses')
      .upsert(analysisData, {
        onConflict: 'user_id,analysis_month',
        ignoreDuplicates: false,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete analysis for a specific month
   * @param {string} month - Format: YYYY-MM
   */
  async deleteAnalysis(month) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('cashflow_analyses')
      .delete()
      .eq('user_id', user.id)
      .eq('analysis_month', month);

    if (error) throw error;
  },

  /**
   * Get all analyses for the user
   */
  async getAllAnalyses() {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('cashflow_analyses')
      .select('*')
      .eq('user_id', user.id)
      .order('analysis_month', { ascending: false });

    if (error) throw error;
    return data;
  },
};

// =====================================================
// CREDIT CARDS
// =====================================================

export const creditCards = {
  // =====================================================
  // STATEMENTS
  // =====================================================

  /**
   * Get all statements for user
   */
  async getStatements(options = {}) {
    const { limit = 20, offset = 0, cardNumber = null } = options;

    let query = supabase
      .from('credit_card_statements')
      .select('*')
      .order('due_date', { ascending: false })
      .range(offset, offset + limit - 1);

    if (cardNumber) {
      query = query.eq('card_number_masked', cardNumber);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  /**
   * Get statement with transactions
   */
  async getStatementWithTransactions(statementId) {
    const { data, error } = await supabase
      .from('credit_card_statements')
      .select(`
        *,
        transactions:credit_card_transactions(*)
      `)
      .eq('id', statementId)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Create statement with transactions
   */
  async createStatement(statementData, transactions) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    // Insert statement
    const { data: statement, error: statementError } = await supabase
      .from('credit_card_statements')
      .insert({
        ...statementData,
        user_id: user.id,
      })
      .select()
      .single();

    if (statementError) throw statementError;

    // Insert transactions with rollback on failure
    if (transactions && transactions.length > 0) {
      // Valid transaction types per database constraint
      const VALID_TRANSACTION_TYPES = ['purchase', 'refund', 'payment', 'fee', 'interest', 'cash_advance'];

      const sanitizeTransactionType = (type) => {
        if (!type) return 'purchase';
        const normalized = type.toLowerCase().trim();
        // Map common variations
        if (normalized === 'withdrawal' || normalized === 'cash') return 'cash_advance';
        if (normalized === 'charge' || normalized === 'debit') return 'purchase';
        if (normalized === 'credit' || normalized === 'return') return 'refund';
        if (normalized === 'faiz' || normalized === 'interest_charge') return 'interest';
        if (normalized === 'masraf' || normalized === 'commission') return 'fee';
        if (normalized === 'ödeme' || normalized === 'odeme') return 'payment';
        // Return if valid, otherwise default to purchase
        return VALID_TRANSACTION_TYPES.includes(normalized) ? normalized : 'purchase';
      };

      // Normalize amount based on transaction type
      // Positive = charges (purchase, fee, interest, cash_advance)
      // Negative = credits (payment, refund)
      const normalizeAmount = (amount, transactionType) => {
        const absAmount = Math.abs(parseFloat(amount) || 0);
        const creditTypes = ['payment', 'refund'];
        return creditTypes.includes(transactionType) ? -absAmount : absAmount;
      };

      const transactionsData = transactions.map(t => {
        const txType = sanitizeTransactionType(t.transactionType);
        return {
        statement_id: statement.id,
        user_id: user.id,
        transaction_date: t.date,
        posting_date: t.postingDate || null,
        merchant_name: t.merchantName,
        merchant_raw: t.merchantRaw || null,
        amount: normalizeAmount(t.amount, txType),
        currency: t.currency || 'TRY',
        original_amount: t.originalAmount || null,
        original_currency: t.originalCurrency || null,
        ai_category: t.category || null,
        ai_subcategory: t.subcategory || null,
        ai_confidence: t.confidence || null,
        transaction_type: txType,
        is_installment: t.isInstallment || false,
        installment_current: t.installmentCurrent || null,
        installment_total: t.installmentTotal || null,
        metadata: t.metadata || {},
        };
      });

      const { error: txError } = await supabase
        .from('credit_card_transactions')
        .insert(transactionsData);

      if (txError) {
        // Rollback: delete the statement we just created
        console.error('Transaction insert failed, rolling back statement:', txError);
        await supabase
          .from('credit_card_statements')
          .delete()
          .eq('id', statement.id);

        throw new Error(`İşlemler kaydedilemedi: ${txError.message}. Ekstre kaydı geri alındı.`);
      }
    }

    return statement;
  },

  /**
   * Update statement status (paid, partial)
   */
  async updateStatementStatus(statementId, status, paidAmount = null) {
    const updates = {
      status,
      paid_at: status === 'paid' ? new Date().toISOString() : null,
      paid_amount: paidAmount,
    };

    const { data, error } = await supabase
      .from('credit_card_statements')
      .update(updates)
      .eq('id', statementId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete statement (cascades to transactions)
   */
  async deleteStatement(statementId) {
    const { error } = await supabase
      .from('credit_card_statements')
      .delete()
      .eq('id', statementId);

    if (error) throw error;
  },

  // =====================================================
  // TRANSACTIONS
  // =====================================================

  /**
   * Get transactions with filters
   */
  async getTransactions(options = {}) {
    const {
      startDate,
      endDate,
      category = null,
      merchant = null,
      limit = 1000, // Increased default for comprehensive analysis
      offset = 0,
    } = options;

    let query = supabase
      .from('credit_card_transactions')
      .select('*, statement:credit_card_statements(card_number_masked, bank_name, card_name)')
      .order('transaction_date', { ascending: false });

    // Only apply range if limit > 0 (0 means no limit)
    if (limit > 0) {
      query = query.range(offset, offset + limit - 1);
    }

    if (startDate) {
      query = query.gte('transaction_date', startDate);
    }
    if (endDate) {
      query = query.lte('transaction_date', endDate);
    }
    if (category) {
      query = query.eq('ai_category', category);
    }
    if (merchant) {
      query = query.ilike('merchant_name', `%${merchant}%`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  /**
   * Update transaction category (user override)
   */
  async updateTransactionCategory(transactionId, category) {
    const { data, error } = await supabase
      .from('credit_card_transactions')
      .update({ user_category: category })
      .eq('id', transactionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get spending summary by category
   */
  async getSpendingByCategory(startDate, endDate) {
    const { data, error } = await supabase
      .from('credit_card_transactions')
      .select('ai_category, user_category, amount')
      .gte('transaction_date', startDate)
      .lte('transaction_date', endDate)
      .in('transaction_type', ['purchase']);

    if (error) throw error;

    // Aggregate by category
    const byCategory = {};
    let total = 0;

    data.forEach(t => {
      const cat = t.user_category || t.ai_category || 'other';
      byCategory[cat] = (byCategory[cat] || 0) + parseFloat(t.amount);
      total += parseFloat(t.amount);
    });

    return Object.entries(byCategory)
      .map(([category, amount]) => ({
        category,
        amount,
        percentage: total > 0 ? parseFloat((amount / total * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.amount - a.amount);
  },

  /**
   * Get top merchants
   */
  async getTopMerchants(startDate, endDate, limit = 10) {
    const { data, error } = await supabase
      .from('credit_card_transactions')
      .select('merchant_name, amount, ai_category')
      .gte('transaction_date', startDate)
      .lte('transaction_date', endDate)
      .in('transaction_type', ['purchase']);

    if (error) throw error;

    // Aggregate by merchant
    const byMerchant = {};

    data.forEach(t => {
      if (!byMerchant[t.merchant_name]) {
        byMerchant[t.merchant_name] = {
          amount: 0,
          count: 0,
          category: t.ai_category,
        };
      }
      byMerchant[t.merchant_name].amount += parseFloat(t.amount);
      byMerchant[t.merchant_name].count += 1;
    });

    return Object.entries(byMerchant)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, limit);
  },

  /**
   * Get unique cards
   */
  async getCards() {
    const { data, error } = await supabase
      .from('credit_card_statements')
      .select('card_number_masked, card_name, bank_name')
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Unique cards
    const uniqueCards = [];
    const seen = new Set();

    data.forEach(s => {
      if (!seen.has(s.card_number_masked)) {
        seen.add(s.card_number_masked);
        uniqueCards.push(s);
      }
    });

    return uniqueCards;
  },

  /**
   * Get statements count for a card
   */
  async getStatementCount(cardNumber = null) {
    let query = supabase
      .from('credit_card_statements')
      .select('id', { count: 'exact', head: true });

    if (cardNumber) {
      query = query.eq('card_number_masked', cardNumber);
    }

    const { count, error } = await query;
    if (error) throw error;
    return count;
  },

  /**
   * Check if a statement already exists (duplicate detection)
   * @param {Object} params - Parameters to check
   * @returns {Object|null} - Existing statement if found, null otherwise
   */
  async checkDuplicate({ cardNumberMasked, periodStart, periodEnd, dueDate, totalBalance }) {
    // Build query to find potential duplicates
    let query = supabase
      .from('credit_card_statements')
      .select('id, card_number_masked, bank_name, statement_period_start, statement_period_end, due_date, total_balance, created_at');

    // Match by card number (last 4 digits)
    if (cardNumberMasked) {
      query = query.ilike('card_number_masked', `%${cardNumberMasked.slice(-4)}`);
    }

    // Match by period OR due date + balance
    if (periodStart && periodEnd) {
      query = query
        .eq('statement_period_start', periodStart)
        .eq('statement_period_end', periodEnd);
    } else if (dueDate && totalBalance) {
      query = query
        .eq('due_date', dueDate)
        .eq('total_balance', totalBalance);
    }

    const { data, error } = await query.limit(1);
    if (error) throw error;

    return data && data.length > 0 ? data[0] : null;
  },

  /**
   * Get total spending for a period
   */
  async getTotalSpending(startDate, endDate) {
    const { data, error } = await supabase
      .from('credit_card_transactions')
      .select('amount, currency')
      .gte('transaction_date', startDate)
      .lte('transaction_date', endDate)
      .in('transaction_type', ['purchase']);

    if (error) throw error;

    const totals = {};
    data.forEach(t => {
      const currency = t.currency || 'TRY';
      totals[currency] = (totals[currency] || 0) + parseFloat(t.amount);
    });

    return totals;
  },
};

// =====================================================
// SPENDING ANALYSIS SERVICE
// =====================================================
export const spendingAnalysis = {
  /**
   * Get the latest analysis for current user
   */
  async getLatest(analysisType = 'credit_card') {
    const { data, error } = await supabase
      .from('spending_analysis')
      .select('*')
      .eq('analysis_type', analysisType)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Get analysis for a specific period
   */
  async getByPeriod(periodStart, periodEnd, analysisType = 'credit_card') {
    const { data, error } = await supabase
      .from('spending_analysis')
      .select('*')
      .eq('period_start', periodStart)
      .eq('period_end', periodEnd)
      .eq('analysis_type', analysisType)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Save or update analysis
   */
  async save(analysisData) {
    const {
      periodStart,
      periodEnd,
      analysisType = 'credit_card',
      statementsAnalyzed = 0,
      transactionsAnalyzed = 0,
      analysisResult,
    } = analysisData;

    // Extract quick access fields from result
    const healthScore = analysisResult?.healthScore || null;
    const totalSpending = analysisResult?.overallAnalysis?.totalPeriodSpending ||
      analysisResult?.monthlyBreakdown?.reduce((sum, m) => sum + (m.totalSpending || 0), 0) || null;
    const totalSubscriptionsMonthly = analysisResult?.subscriptions?.totalMonthly || null;
    const topCategory = analysisResult?.overallAnalysis?.spendingByCategory?.[0]?.category ||
      analysisResult?.spendingByCategory?.[0]?.category || null;

    // Upsert (insert or update on conflict)
    const { data, error } = await supabase
      .from('spending_analysis')
      .upsert({
        user_id: (await supabase.auth.getUser()).data.user?.id,
        period_start: periodStart,
        period_end: periodEnd,
        analysis_type: analysisType,
        statements_analyzed: statementsAnalyzed,
        transactions_analyzed: transactionsAnalyzed,
        analysis_result: analysisResult,
        health_score: healthScore,
        total_spending: totalSpending,
        total_subscriptions_monthly: totalSubscriptionsMonthly,
        top_category: topCategory,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,period_start,period_end,analysis_type',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete analysis
   */
  async delete(id) {
    const { error } = await supabase
      .from('spending_analysis')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Get analysis history
   */
  async getHistory(limit = 10) {
    const { data, error } = await supabase
      .from('spending_analysis')
      .select('id, period_start, period_end, analysis_type, health_score, total_spending, created_at')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  /**
   * Check if analysis is stale (older than specified hours)
   */
  isStale(analysis, maxAgeHours = 24) {
    if (!analysis?.updated_at) return true;
    const updatedAt = new Date(analysis.updated_at);
    const now = new Date();
    const hoursDiff = (now - updatedAt) / (1000 * 60 * 60);
    return hoursDiff > maxAgeHours;
  },
};

// =====================================================
// MOODMAP - Daily Reflection Journal
// =====================================================

export const moodmap = {
  // =====================================================
  // ENTRIES
  // =====================================================

  /**
   * Get entries for a date range
   */
  async getEntries(startDate, endDate) {
    const { data, error } = await supabase
      .from('moodmap_entries')
      .select(`
        *,
        items:moodmap_items(*)
      `)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });
    if (error) throw error;
    return data;
  },

  /**
   * Get all entries for current user (for yearly heatmap)
   */
  async getAllEntries(year = null) {
    let query = supabase
      .from('moodmap_entries')
      .select('id, date, mood')
      .order('date', { ascending: true });

    if (year) {
      query = query
        .gte('date', `${year}-01-01`)
        .lte('date', `${year}-12-31`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  /**
   * Get a single entry by date
   */
  async getEntryByDate(date) {
    const { data, error } = await supabase
      .from('moodmap_entries')
      .select(`
        *,
        items:moodmap_items(
          *,
          tag:moodmap_tags(*)
        )
      `)
      .eq('date', date)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Get entry by ID
   */
  async getEntry(entryId) {
    const { data, error } = await supabase
      .from('moodmap_entries')
      .select(`
        *,
        items:moodmap_items(
          *,
          tag:moodmap_tags(*)
        )
      `)
      .eq('id', entryId)
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Create a new entry
   */
  async createEntry(entryData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('moodmap_entries')
      .insert({ ...entryData, user_id: user.id })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Update an entry
   */
  async updateEntry(entryId, updates) {
    const { data, error } = await supabase
      .from('moodmap_entries')
      .update(updates)
      .eq('id', entryId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Delete an entry
   */
  async deleteEntry(entryId) {
    const { error } = await supabase
      .from('moodmap_entries')
      .delete()
      .eq('id', entryId);
    if (error) throw error;
  },

  /**
   * Upsert entry (create or update by date)
   */
  async upsertEntry(date, entryData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('moodmap_entries')
      .upsert(
        { ...entryData, date, user_id: user.id },
        { onConflict: 'user_id,date' }
      )
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // =====================================================
  // ITEMS
  // =====================================================

  /**
   * Add item to an entry
   */
  async addItem(entryId, itemData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('moodmap_items')
      .insert({
        ...itemData,
        entry_id: entryId,
        user_id: user.id,
      })
      .select(`
        *,
        tag:moodmap_tags(*)
      `)
      .single();
    if (error) throw error;

    // Increment tag usage if tag is used
    if (itemData.tag_id) {
      await this.incrementTagUsage(itemData.tag_id);
    }

    return data;
  },

  /**
   * Update an item
   */
  async updateItem(itemId, updates) {
    const { data, error } = await supabase
      .from('moodmap_items')
      .update(updates)
      .eq('id', itemId)
      .select(`
        *,
        tag:moodmap_tags(*)
      `)
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Delete an item
   */
  async deleteItem(itemId) {
    const { error } = await supabase
      .from('moodmap_items')
      .delete()
      .eq('id', itemId);
    if (error) throw error;
  },

  /**
   * Add multiple items at once
   */
  async addItems(entryId, items) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const itemsData = items.map(item => ({
      ...item,
      entry_id: entryId,
      user_id: user.id,
    }));

    const { data, error } = await supabase
      .from('moodmap_items')
      .insert(itemsData)
      .select(`
        *,
        tag:moodmap_tags(*)
      `);
    if (error) throw error;

    // Increment tag usage for all used tags
    const tagIds = items.filter(i => i.tag_id).map(i => i.tag_id);
    for (const tagId of tagIds) {
      await this.incrementTagUsage(tagId);
    }

    return data;
  },

  // =====================================================
  // TAGS
  // =====================================================

  /**
   * Get all available tags (system + user's)
   */
  async getTags() {
    const { data, error } = await supabase
      .from('moodmap_tags')
      .select('*')
      .order('usage_count', { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * Get tags by category
   */
  async getTagsByCategory(category) {
    const { data, error } = await supabase
      .from('moodmap_tags')
      .select('*')
      .or(`category.eq.${category},category.eq.general`)
      .order('usage_count', { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * Get smart tag suggestions (most used)
   */
  async getSmartTagSuggestions(limit = 8) {
    const { data, error } = await supabase
      .from('moodmap_tags')
      .select('*')
      .order('usage_count', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return data;
  },

  /**
   * Create a custom tag
   */
  async createTag(tagData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('moodmap_tags')
      .insert({
        ...tagData,
        user_id: user.id,
        is_system: false,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Increment tag usage count
   */
  async incrementTagUsage(tagId) {
    const { error } = await supabase
      .from('moodmap_tags')
      .update({
        usage_count: supabase.rpc('increment', { x: 1 }),
        last_used_at: new Date().toISOString(),
      })
      .eq('id', tagId);

    // Fallback: direct increment if rpc doesn't work
    if (error) {
      const { data: tag } = await supabase
        .from('moodmap_tags')
        .select('usage_count')
        .eq('id', tagId)
        .single();

      if (tag) {
        await supabase
          .from('moodmap_tags')
          .update({
            usage_count: (tag.usage_count || 0) + 1,
            last_used_at: new Date().toISOString(),
          })
          .eq('id', tagId);
      }
    }
  },

  /**
   * Delete a custom tag
   */
  async deleteTag(tagId) {
    const { error } = await supabase
      .from('moodmap_tags')
      .delete()
      .eq('id', tagId);
    if (error) throw error;
  },

  // =====================================================
  // STATS
  // =====================================================

  /**
   * Get user's current streak
   */
  async getStreak() {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .rpc('get_moodmap_streak', { p_user_id: user.id });

    if (error) {
      // Fallback: calculate client-side
      console.warn('Streak RPC failed, calculating client-side:', error);
      return this.calculateStreakClientSide();
    }
    return data;
  },

  /**
   * Calculate streak client-side (fallback)
   */
  async calculateStreakClientSide() {
    const today = new Date();
    let streak = 0;
    let checkDate = new Date(today);

    while (true) {
      const dateStr = checkDate.toISOString().split('T')[0];
      const { data } = await supabase
        .from('moodmap_entries')
        .select('id')
        .eq('date', dateStr)
        .single();

      if (data) {
        streak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        break;
      }

      // Safety limit
      if (streak > 365) break;
    }

    return streak;
  },

  /**
   * Get mood distribution for a period
   */
  async getMoodDistribution(startDate, endDate) {
    const { data, error } = await supabase
      .from('moodmap_entries')
      .select('mood')
      .gte('date', startDate)
      .lte('date', endDate)
      .not('mood', 'is', null);

    if (error) throw error;

    const distribution = {
      great: 0,
      good: 0,
      neutral: 0,
      bad: 0,
      awful: 0,
    };

    data.forEach(entry => {
      if (entry.mood && distribution[entry.mood] !== undefined) {
        distribution[entry.mood]++;
      }
    });

    return distribution;
  },

  /**
   * Get category stats for a period
   */
  async getCategoryStats(startDate, endDate) {
    const { data, error } = await supabase
      .from('moodmap_items')
      .select(`
        category,
        entry:moodmap_entries!inner(date)
      `)
      .gte('entry.date', startDate)
      .lte('entry.date', endDate);

    if (error) throw error;

    const stats = {
      win: 0,
      struggle: 0,
      learning: 0,
      grateful: 0,
    };

    data.forEach(item => {
      if (item.category && stats[item.category] !== undefined) {
        stats[item.category]++;
      }
    });

    return stats;
  },
};

// =====================================================
// FITNESS TRACKER
// =====================================================

export const fitness = {
  // =====================================================
  // GOOGLE OAUTH (via Edge Function)
  // =====================================================

  /**
   * Check if Google account is connected
   */
  async isGoogleConnected() {
    const user = await getCachedUser();
    if (!user) return { connected: false };

    const { data, error } = await supabase
      .from('user_google_tokens')
      .select('google_email, token_expiry')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error || !data) return { connected: false };
    return { connected: true, email: data.google_email };
  },

  /**
   * Get Google OAuth authorization URL
   */
  async getGoogleAuthUrl() {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const state = btoa(JSON.stringify({ userId: user.id, timestamp: Date.now() }));

    const { data, error } = await supabase.functions.invoke('google-sheets', {
      body: { action: 'get-auth-url', payload: { state } },
    });

    if (error) throw error;
    return { ...data, state };
  },

  /**
   * Exchange authorization code for tokens
   */
  async exchangeGoogleCode(code) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('google-sheets', {
      body: { action: 'exchange-code', payload: { code, userId: user.id } },
    });

    if (error) throw error;
    return data;
  },

  /**
   * Disconnect Google account
   */
  async disconnectGoogle() {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('google-sheets', {
      body: { action: 'disconnect', payload: { userId: user.id } },
    });

    if (error) throw error;
    return data;
  },

  // =====================================================
  // SPREADSHEETS
  // =====================================================

  /**
   * List available Google Spreadsheets
   */
  async listGoogleSpreadsheets() {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('google-sheets', {
      body: { action: 'list-spreadsheets', payload: { userId: user.id } },
    });

    if (error) throw error;
    return data.spreadsheets || [];
  },

  /**
   * Read data from a spreadsheet range
   */
  async readSheetData(spreadsheetId, range) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('google-sheets', {
      body: { action: 'read-sheet', payload: { userId: user.id, spreadsheetId, range } },
    });

    if (error) throw error;
    return data;
  },

  /**
   * Append a row to a spreadsheet
   */
  async appendWorkoutToSheet(spreadsheetId, sheetName, values) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('google-sheets', {
      body: {
        action: 'append-row',
        payload: { userId: user.id, spreadsheetId, sheetName, values },
      },
    });

    if (error) throw error;
    return data;
  },

  /**
   * Connect a spreadsheet with schema mapping
   */
  async connectSpreadsheet(spreadsheetId, spreadsheetName, schemaMapping) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('fitness_spreadsheets')
      .upsert({
        user_id: user.id,
        spreadsheet_id: spreadsheetId,
        spreadsheet_name: spreadsheetName,
        schema_mapping: schemaMapping,
      }, { onConflict: 'user_id,spreadsheet_id' })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get connected spreadsheets
   */
  async getConnectedSpreadsheets() {
    const { data, error } = await supabase
      .from('fitness_spreadsheets')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Update spreadsheet sync status
   */
  async updateSpreadsheetSyncStatus(id, status, error = null) {
    const updates = {
      sync_status: status,
      sync_error: error,
    };
    if (status === 'synced') {
      updates.last_synced_at = new Date().toISOString();
    }

    const { data, error: dbError } = await supabase
      .from('fitness_spreadsheets')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (dbError) throw dbError;
    return data;
  },

  // =====================================================
  // WORKOUTS
  // =====================================================

  /**
   * Get workouts for a date range
   */
  async getWorkouts(startDate, endDate, options = {}) {
    let query = supabase
      .from('fitness_workouts')
      .select('*')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false });

    if (options.exerciseName) {
      query = query.eq('exercise_name', options.exerciseName);
    }
    if (options.exerciseType) {
      query = query.eq('exercise_type', options.exerciseType);
    }
    if (options.muscleGroup) {
      query = query.eq('muscle_group', options.muscleGroup);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Create a new workout
   */
  async createWorkout(workoutData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('fitness_workouts')
      .insert({ ...workoutData, user_id: user.id })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update a workout
   */
  async updateWorkout(workoutId, updates) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    console.log('[updateWorkout] Updating workout:', workoutId, 'with:', updates);

    const { data, error } = await supabase
      .from('fitness_workouts')
      .update(updates)
      .eq('id', workoutId)
      .eq('user_id', user.id)
      .select()
      .maybeSingle();

    if (error) {
      console.error('[updateWorkout] Error:', error);
      throw error;
    }

    if (!data) {
      console.warn('[updateWorkout] No data returned - workout may not exist or permission denied');
      throw new Error('Workout not found or permission denied');
    }

    console.log('[updateWorkout] Success:', data);
    return data;
  },

  /**
   * Delete a workout
   */
  async deleteWorkout(workoutId) {
    const { error } = await supabase
      .from('fitness_workouts')
      .delete()
      .eq('id', workoutId);

    if (error) throw error;
  },

  /**
   * Get workout linked to a scheduled workout
   * Returns the most recent one if multiple exist
   */
  async getWorkoutByScheduledId(scheduledWorkoutId) {
    const { data, error } = await supabase
      .from('fitness_workouts')
      .select('*')
      .eq('scheduled_workout_id', scheduledWorkoutId)
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) throw error;
    return data?.[0] || null;
  },

  /**
   * Get ALL workouts linked to a scheduled workout
   * Used to display completed workout exercises
   */
  async getAllWorkoutsByScheduledId(scheduledWorkoutId) {
    const { data, error } = await supabase
      .from('fitness_workouts')
      .select('*')
      .eq('scheduled_workout_id', scheduledWorkoutId)
      .order('exercise_name', { ascending: true })
      .order('row_order', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Create a normalized key for duplicate detection
   * Ensures consistent comparison regardless of type (string vs number)
   */
  _createWorkoutKey(w) {
    const date = w.date || '';
    const name = (w.exercise_name || '').trim().toLowerCase();
    const reps = w.reps ? parseInt(w.reps) : 0;
    const weight = w.weight ? parseFloat(w.weight).toFixed(1) : '0.0';
    return `${date}|${name}|${reps}|${weight}`;
  },

  /**
   * Get existing workouts for duplicate detection
   * Returns a Set of normalized keys
   * @param {string} userId - Optional user ID (for coach mode viewing client's workouts)
   */
  async getExistingWorkoutKeys(userId = null) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const targetUserId = userId || user.id;

    const { data, error } = await supabase
      .from('fitness_workouts')
      .select('date, exercise_name, reps, weight')
      .eq('user_id', targetUserId);

    if (error) throw error;

    const keys = new Set();
    (data || []).forEach(w => {
      keys.add(this._createWorkoutKey(w));
    });
    return keys;
  },

  /**
   * Import workouts from a spreadsheet (with duplicate check)
   * @param {Array} workouts - Array of workout objects to import
   * @param {string} targetUserId - Optional user ID (for coach importing for a client)
   */
  async importWorkoutsWithDuplicateCheck(workouts, targetUserId = null) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const userId = targetUserId || user.id;

    // Get existing workout keys for the target user
    const existingKeys = await this.getExistingWorkoutKeys(userId);
    console.log('=== EXISTING KEYS COUNT ===', existingKeys.size);
    console.log('=== SAMPLE EXISTING KEYS ===', [...existingKeys].slice(0, 5));

    // Filter out duplicates using normalized key
    const skippedWorkouts = [];
    const newWorkouts = workouts.filter(w => {
      const key = this._createWorkoutKey(w);
      const isDuplicate = existingKeys.has(key);
      if (isDuplicate) {
        skippedWorkouts.push({ date: w.date, exercise: w.exercise_name, reps: w.reps, weight: w.weight, key });
      }
      return !isDuplicate;
    });

    console.log('=== NEW WORKOUTS ===', newWorkouts.length);
    console.log('=== SKIPPED (DUPLICATES) ===', skippedWorkouts.length);
    if (skippedWorkouts.length > 0 && skippedWorkouts.length < 20) {
      console.log('Skipped details:', skippedWorkouts);
    }

    if (newWorkouts.length === 0) {
      return { imported: 0, skipped: workouts.length };
    }

    // Insert new workouts
    const toInsert = newWorkouts.map(w => ({
      ...w,
      user_id: userId,
      source: 'sheets_import',
    }));

    const { data, error } = await supabase
      .from('fitness_workouts')
      .insert(toInsert)
      .select();

    if (error) throw error;
    return { imported: data.length, skipped: workouts.length - newWorkouts.length };
  },

  /**
   * Import workouts from a spreadsheet
   */
  async importWorkoutsFromSheet(spreadsheetDbId, parsedWorkouts) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const workouts = parsedWorkouts.map(row => ({
      ...row,
      user_id: user.id,
      spreadsheet_id: spreadsheetDbId,
      source: 'sheets_import',
    }));

    const { data, error } = await supabase
      .from('fitness_workouts')
      .insert(workouts)
      .select();

    if (error) throw error;
    return data;
  },

  /**
   * Get unique exercise names for autocomplete
   * Returns normalized names (canonical names for aliased exercises)
   */
  async getExerciseNames() {
    // Try to use the normalized RPC first
    const { data: normalizedData, error: rpcError } = await supabase
      .rpc('get_normalized_exercise_names');

    if (!rpcError && normalizedData) {
      return normalizedData.map(d => d.exercise_name);
    }

    // Fallback to direct query if RPC not available
    console.log('[getExerciseNames] RPC failed, using fallback:', rpcError);
    const { data, error } = await supabase
      .from('fitness_workouts')
      .select('exercise_name')
      .order('exercise_name');

    if (error) throw error;

    const uniqueNames = [...new Set(data.map(d => d.exercise_name))];
    return uniqueNames;
  },

  // =====================================================
  // FAVORITE EXERCISES
  // =====================================================

  /**
   * Get user's favorite exercises
   */
  async getFavoriteExercises() {
    const { data, error } = await supabase
      .from('favorite_exercises')
      .select('exercise_name')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data.map(d => d.exercise_name);
  },

  /**
   * Add exercise to favorites
   */
  async addFavoriteExercise(exerciseName) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('favorite_exercises')
      .insert({ user_id: user.id, exercise_name: exerciseName })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Remove exercise from favorites
   */
  async removeFavoriteExercise(exerciseName) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('favorite_exercises')
      .delete()
      .eq('user_id', user.id)
      .eq('exercise_name', exerciseName);

    if (error) throw error;
    return true;
  },

  // =====================================================
  // SCHEDULED WORKOUTS
  // =====================================================

  /**
   * Get scheduled workouts for a date range
   * Includes coach info if workout was created by a coach
   */
  async getScheduledWorkouts(startDate, endDate) {
    const { data, error } = await supabase
      .from('scheduled_workouts')
      .select(`
        *,
        coach:profiles!scheduled_workouts_coach_id_fkey(id, display_name, email, avatar_url),
        client:profiles!scheduled_workouts_user_id_profiles_fkey(id, display_name, email, avatar_url)
      `)
      .gte('scheduled_date', startDate)
      .lte('scheduled_date', endDate)
      .order('scheduled_date', { ascending: true })
      .order('scheduled_time', { ascending: true });

    // If join fails, try without join
    if (error) {
      console.warn('Join query failed, falling back to simple query:', error.message);
      const { data: simpleData, error: simpleError } = await supabase
        .from('scheduled_workouts')
        .select('*')
        .gte('scheduled_date', startDate)
        .lte('scheduled_date', endDate)
        .order('scheduled_date', { ascending: true })
        .order('scheduled_time', { ascending: true });

      if (simpleError) throw simpleError;
      return simpleData || [];
    }

    // Map coach and client info for display
    return (data || []).map(workout => ({
      ...workout,
      coach_name: workout.coach?.display_name || workout.coach?.email || null,
      coach_avatar_url: workout.coach?.avatar_url || null,
      client_name: workout.client?.display_name || workout.client?.email || null,
      client_avatar_url: workout.client?.avatar_url || null,
    }));
  },

  /**
   * Get scheduled workouts for a specific day
   */
  async getScheduledWorkoutsForDay(date) {
    const { data, error } = await supabase
      .from('scheduled_workouts')
      .select('*')
      .eq('scheduled_date', date)
      .order('scheduled_time', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Create a scheduled workout
   */
  async createScheduledWorkout(workoutData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('scheduled_workouts')
      .insert({ ...workoutData, user_id: user.id })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update a scheduled workout
   */
  async updateScheduledWorkout(workoutId, updates) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('scheduled_workouts')
      .update(updates)
      .eq('id', workoutId)
      .eq('user_id', user.id)
      .select()
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Delete a scheduled workout
   */
  async deleteScheduledWorkout(workoutId) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('scheduled_workouts')
      .delete()
      .eq('id', workoutId)
      .eq('user_id', user.id);

    if (error) throw error;
  },


  /**
   * Mark scheduled workout as missed
   */
  async markScheduledWorkoutMissed(scheduledId) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('scheduled_workouts')
      .update({ status: 'missed' })
      .eq('id', scheduledId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // =====================================================
  // EXERCISE NORMALIZATION
  // =====================================================

  /**
   * Get all exercises from lookup table
   */
  async getExercises() {
    const { data, error } = await supabase
      .from('fitness_exercises')
      .select('*')
      .order('canonical_name');

    if (error) throw error;
    return data || [];
  },

  /**
   * Get exercise by canonical name
   */
  async getExerciseByName(canonicalName) {
    const { data, error } = await supabase
      .from('fitness_exercises')
      .select('*')
      .ilike('canonical_name', canonicalName)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Create or update exercise in lookup table
   */
  async upsertExercise(exercise) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('fitness_exercises')
      .upsert({
        user_id: user.id,
        canonical_name: exercise.canonical_name,
        aliases: exercise.aliases || exercise.original_names || [],
        primary_muscle_group: exercise.primary_muscle_group,
        secondary_muscle_groups: exercise.secondary_muscle_groups || [],
        exercise_type: exercise.exercise_type || 'strength',
        is_compound: exercise.is_compound || false,
        equipment: exercise.equipment || [],
        ai_normalized: true,
        ai_confidence: 0.9,
      }, { onConflict: 'user_id,canonical_name' })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Normalize exercise names using AI
   */
  async normalizeExerciseNames(exerciseNames) {
    // Get existing exercises for context
    const existingExercises = await this.getExercises();

    const { data, error } = await supabase.functions.invoke('gemini', {
      body: {
        action: 'normalize-exercises',
        payload: {
          exerciseNames,
          existingExercises: existingExercises.map(e => ({
            canonical_name: e.canonical_name,
            aliases: e.aliases || [],
          })),
        },
      },
    });

    if (error) throw error;
    return data;
  },

  /**
   * Normalize exercises and save to lookup table
   */
  async normalizeAndSaveExercises(exerciseNames) {
    // Call AI to normalize
    const normalized = await this.normalizeExerciseNames(exerciseNames);

    // Save each exercise to lookup table
    const savedExercises = [];
    for (const exercise of normalized.exercises || []) {
      try {
        const saved = await this.upsertExercise(exercise);
        savedExercises.push(saved);
      } catch (err) {
        console.error('Failed to save exercise:', exercise.canonical_name, err);
      }
    }

    return {
      exercises: savedExercises,
      mappings: normalized.mappings || {},
    };
  },

  // =====================================================
  // PERSONAL RECORDS
  // =====================================================

  /**
   * Get personal records
   */
  async getPRs(exerciseName = null) {
    let query = supabase
      .from('fitness_personal_records')
      .select('*')
      .order('achieved_at', { ascending: false });

    if (exerciseName) {
      query = query.eq('exercise_name', exerciseName);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Check and update PRs after a workout
   */
  async checkAndUpdatePRs(workout) {
    const user = await getCachedUser();
    if (!user) return null;

    const newPRs = [];

    // Check max weight PR
    if (workout.weight) {
      const { data: existingPR } = await supabase
        .from('fitness_personal_records')
        .select('*')
        .eq('user_id', user.id)
        .eq('exercise_name', workout.exercise_name)
        .eq('record_type', 'max_weight')
        .maybeSingle();

      if (!existingPR || workout.weight > existingPR.record_value) {
        const { data } = await supabase
          .from('fitness_personal_records')
          .upsert({
            user_id: user.id,
            exercise_name: workout.exercise_name,
            record_type: 'max_weight',
            record_value: workout.weight,
            record_unit: workout.weight_unit || 'kg',
            achieved_at: workout.date,
            workout_id: workout.id,
            previous_record: existingPR?.record_value,
            improvement_percent: existingPR
              ? ((workout.weight - existingPR.record_value) / existingPR.record_value * 100)
              : null,
          }, { onConflict: 'user_id,exercise_name,record_type' })
          .select()
          .maybeSingle();

        if (data) newPRs.push(data);
      }
    }

    // Check max reps PR
    if (workout.reps) {
      const { data: existingPR } = await supabase
        .from('fitness_personal_records')
        .select('*')
        .eq('user_id', user.id)
        .eq('exercise_name', workout.exercise_name)
        .eq('record_type', 'max_reps')
        .maybeSingle();

      if (!existingPR || workout.reps > existingPR.record_value) {
        const { data } = await supabase
          .from('fitness_personal_records')
          .upsert({
            user_id: user.id,
            exercise_name: workout.exercise_name,
            record_type: 'max_reps',
            record_value: workout.reps,
            achieved_at: workout.date,
            workout_id: workout.id,
            previous_record: existingPR?.record_value,
          }, { onConflict: 'user_id,exercise_name,record_type' })
          .select()
          .maybeSingle();

        if (data) newPRs.push(data);
      }
    }

    return newPRs;
  },

  // =====================================================
  // GOALS
  // =====================================================

  /**
   * Get goals
   */
  async getGoals(status = null) {
    let query = supabase
      .from('fitness_goals')
      .select('*')
      .order('target_date', { ascending: true });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Create a goal
   */
  async createGoal(goalData) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('fitness_goals')
      .insert({ ...goalData, user_id: user.id })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update a goal
   */
  async updateGoal(goalId, updates) {
    const { data, error } = await supabase
      .from('fitness_goals')
      .update(updates)
      .eq('id', goalId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Complete a goal
   */
  async completeGoal(goalId) {
    const { data, error } = await supabase
      .from('fitness_goals')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
      })
      .eq('id', goalId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete a goal
   */
  async deleteGoal(goalId) {
    const { error } = await supabase
      .from('fitness_goals')
      .delete()
      .eq('id', goalId);

    if (error) throw error;
  },

  // =====================================================
  // ANALYTICS
  // =====================================================

  /**
   * Get workout streak
   */
  async getStreak() {
    const user = await getCachedUser();
    if (!user) return 0;

    const { data, error } = await supabase.rpc('get_fitness_streak', {
      p_user_id: user.id,
    });

    if (error) {
      console.error('Error getting streak:', error);
      return 0;
    }
    return data || 0;
  },

  /**
   * Get weekly workout count
   */
  async getWeeklyWorkoutCount() {
    const user = await getCachedUser();
    if (!user) return 0;

    const { data, error } = await supabase.rpc('get_weekly_workout_count', {
      p_user_id: user.id,
    });

    if (error) {
      console.error('Error getting weekly count:', error);
      return 0;
    }
    return data || 0;
  },

  /**
   * Get exercise progress over time
   */
  async getExerciseProgress(exerciseName, days = 90) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('fitness_workouts')
      .select('date, sets, reps, weight, weight_unit')
      .eq('exercise_name', exerciseName)
      .gte('date', startDate.toISOString().split('T')[0])
      .order('date', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Search exercise history by name (case-insensitive partial match)
   * Returns all matching workouts sorted by date (newest first)
   * @param {string} searchTerm - Search term to match against exercise names
   * @param {string} userId - Optional user ID (for coach mode)
   */
  async searchExerciseHistory(searchTerm, userId = null) {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return [];
    }

    const user = userId ? { id: userId } : await getCachedUser();
    if (!user) {
      console.log('[searchExerciseHistory] No user found');
      return [];
    }

    const { data, error } = await supabase
      .from('fitness_workouts')
      .select('id, date, exercise_name, sets, reps, weight, weight_unit, exercise_type, muscle_group, notes, row_order')
      .eq('user_id', user.id)
      .ilike('exercise_name', `%${searchTerm.trim()}%`)
      .order('date', { ascending: false })
      .order('row_order', { ascending: true });

    if (error) {
      console.error('[searchExerciseHistory] Error:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Get previous workout sets for an exercise (for comparison)
   * Returns sets from the most recent workout before the given date
   * @param {string} exerciseName - Name of the exercise
   * @param {string} beforeDate - Date string (YYYY-MM-DD) to look before
   * @param {string} userId - Optional user ID (for coach mode)
   */
  async getPreviousWorkoutSets(exerciseName, beforeDate, userId = null) {
    const user = userId ? { id: userId } : await getCachedUser();
    if (!user) {
      console.log('[getPreviousWorkoutSets] No user found');
      return [];
    }

    console.log('[getPreviousWorkoutSets] Querying for:', {
      exerciseName,
      beforeDate,
      userId: user.id,
    });

    // First, find the most recent date this exercise was done before the given date
    const { data: lastWorkoutDate, error: dateError } = await supabase
      .from('fitness_workouts')
      .select('date')
      .eq('user_id', user.id)
      .eq('exercise_name', exerciseName)
      .lt('date', beforeDate)
      .order('date', { ascending: false })
      .limit(1)
      .maybeSingle();

    console.log('[getPreviousWorkoutSets] Last workout date result:', { lastWorkoutDate, dateError });

    if (dateError || !lastWorkoutDate) return [];

    // Then get all sets from that date for this exercise
    const { data, error } = await supabase
      .from('fitness_workouts')
      .select('id, date, reps, weight, weight_unit, row_order')
      .eq('user_id', user.id)
      .eq('exercise_name', exerciseName)
      .eq('date', lastWorkoutDate.date)
      .order('row_order', { ascending: true });

    if (error) {
      console.error('Error fetching previous workout sets:', error);
      return [];
    }

    return data || [];
  },

  /**
   * Get workout frequency by date
   */
  async getWorkoutFrequency(days = 30) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('fitness_workouts')
      .select('date, exercise_type, muscle_group')
      .gte('date', startDate.toISOString().split('T')[0]);

    if (error) throw error;

    // Group by date
    const byDate = {};
    (data || []).forEach(w => {
      if (!byDate[w.date]) {
        byDate[w.date] = { count: 0, types: new Set(), muscleGroups: new Set() };
      }
      byDate[w.date].count++;
      if (w.exercise_type) byDate[w.date].types.add(w.exercise_type);
      if (w.muscle_group) byDate[w.date].muscleGroups.add(w.muscle_group);
    });

    // Convert sets to arrays for JSON serialization
    Object.keys(byDate).forEach(date => {
      byDate[date].types = [...byDate[date].types];
      byDate[date].muscleGroups = [...byDate[date].muscleGroups];
    });

    return byDate;
  },

  /**
   * Get muscle group volume distribution
   */
  async getMuscleGroupVolume(days = 7) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('fitness_workouts')
      .select('muscle_group, sets, reps, weight')
      .gte('date', startDate.toISOString().split('T')[0]);

    if (error) throw error;

    // Calculate volume by muscle group
    const byMuscle = {};
    (data || []).forEach(w => {
      if (!w.muscle_group) return;
      if (!byMuscle[w.muscle_group]) {
        byMuscle[w.muscle_group] = { volume: 0, sets: 0, workouts: 0 };
      }
      byMuscle[w.muscle_group].sets += w.sets || 0;
      byMuscle[w.muscle_group].volume += (w.sets || 0) * (w.reps || 0) * (w.weight || 1);
      byMuscle[w.muscle_group].workouts++;
    });

    return byMuscle;
  },

  /**
   * Get summary stats
   */
  async getSummaryStats() {
    const [streak, weeklyCount, prs, goals] = await Promise.all([
      this.getStreak(),
      this.getWeeklyWorkoutCount(),
      this.getPRs(),
      this.getGoals('active'),
    ]);

    // Get recent PRs (last 7 days)
    const recentPRs = prs.filter(pr => {
      const prDate = new Date(pr.achieved_at);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return prDate >= weekAgo;
    });

    return {
      streak,
      weeklyWorkouts: weeklyCount,
      totalPRs: prs.length,
      recentPRs: recentPRs.length,
      activeGoals: goals.length,
    };
  },

  // =====================================================
  // AI ANALYSIS
  // =====================================================

  /**
   * Use AI to analyze spreadsheet structure and suggest column mappings
   */
  async analyzeSheetSchema(values, spreadsheetName) {
    const { data, error } = await supabase.functions.invoke('gemini', {
      body: {
        action: 'analyze-fitness-sheet',
        payload: { values, spreadsheetName },
      },
    });

    if (error) throw error;
    return data;
  },

  /**
   * Use AI to parse fitness data using schema mapping
   */
  async parseFitnessData(values, schemaMapping) {
    const { data, error } = await supabase.functions.invoke('gemini', {
      body: {
        action: 'parse-fitness-data',
        payload: { values, schemaMapping },
      },
    });

    if (error) throw error;
    return data;
  },

  /**
   * Smart parse complex spreadsheet formats (wide format with sets in columns)
   * READ-ONLY - does not modify the original sheet
   */
  async smartParseFitnessSheet(values, spreadsheetName) {
    const { data, error } = await supabase.functions.invoke('gemini', {
      body: {
        action: 'smart-parse-fitness-sheet',
        payload: { values, spreadsheetName },
      },
    });

    if (error) throw error;
    return data;
  },

  // =====================================================
  // AI ANALYSIS HISTORY
  // =====================================================

  /**
   * Save AI fitness analysis to history
   */
  async saveAnalysis(analysis, stats, dateRange) {
    const user = await getCachedUser();
    if (!user) throw new Error('Not authenticated');

    const periodEnd = new Date();
    const periodStart = new Date();
    periodStart.setDate(periodStart.getDate() - (dateRange >= 9999 ? stats.actualDaysSpan : dateRange));

    const { data, error } = await supabase
      .from('fitness_ai_analyses')
      .upsert({
        user_id: user.id,
        date_range_days: dateRange,
        period_start: periodStart.toISOString().split('T')[0],
        period_end: periodEnd.toISOString().split('T')[0],
        stats_snapshot: {
          totalDays: stats.totalDays,
          totalVolume: stats.totalVolume,
          totalSets: stats.totalSets,
          totalReps: stats.totalReps,
          uniqueExercises: stats.uniqueExercises,
          avgPerWeek: stats.avgPerWeek,
          muscleGroups: stats.muscleGroups,
        },
        summary: analysis.summary,
        insights: analysis.insights || [],
        recommendations: analysis.recommendations || [],
        technical_notes: analysis.technicalNotes || [],
        motivational_message: analysis.motivationalMessage,
      }, {
        onConflict: 'user_id,date_range_days,period_start',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get latest AI analysis for a date range
   */
  async getLatestAnalysis(dateRange) {
    const user = await getCachedUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('fitness_ai_analyses')
      .select('*')
      .eq('user_id', user.id)
      .eq('date_range_days', dateRange)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error loading analysis:', error);
      return null;
    }
    return data;
  },

  /**
   * Get all AI analyses history
   */
  async getAnalysisHistory(limit = 10) {
    const user = await getCachedUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('fitness_ai_analyses')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error loading analysis history:', error);
      return [];
    }
    return data || [];
  },
};

// Export default object with all services
export default {
  supabase,
  auth,
  profiles,
  maps,
  nodes,
  edges,
  vectorSearch,
  timeTravel,
  chat,
  submaps,
  attachments,
  cashflow,
  creditCards,
  spendingAnalysis,
  moodmap,
  fitness,
};
